/**
 * Enrolled Children Report Component
 * Generates a printable/emailable report of all enrolled children
 * Features:
 * - Simple Sign In logo header
 * - All enrolled children names
 * - Optional toggle to show category (Full Time, School Age, Part Time, After School)
 */
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { Printer, Mail, FileText } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EnrolledChildrenReport = ({ open, onOpenChange }) => {
  const { organization } = useAuth();
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [sending, setSending] = useState(false);
  
  // Toggle option - only category now
  const [showCategory, setShowCategory] = useState(false);
  
  // Category filters
  const [filterFullTime, setFilterFullTime] = useState(true);
  const [filterSchoolAge, setFilterSchoolAge] = useState(true);
  const [filterPartTime, setFilterPartTime] = useState(true);
  const [filterAfterSchool, setFilterAfterSchool] = useState(true);

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const childrenRes = await axios.get(`${API}/children`);
      
      // Filter only currently-enrolled children (matches dashboard/Manage Children)
      const enrolled = (childrenRes.data.children || childrenRes.data || [])
        .filter(c => c.active !== false);
      setChildren(enrolled);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load children data");
    } finally {
      setLoading(false);
    }
  };

  // Get child's category label
  const getCategoryLabel = (child) => {
    const category = (child.category || '').toLowerCase();
    const subcategory = (child.fullTimeSubcategory || child.subcategory || '').toLowerCase();
    
    if (category.includes('school') || subcategory.includes('school')) {
      return 'School Age';
    }
    if (category.includes('part') || subcategory.includes('part')) {
      return 'Part Time';
    }
    if (category.includes('after') || subcategory.includes('after')) {
      return 'After School';
    }
    return 'Full Time';
  };

  // Check if child matches current filters
  const matchesFilter = (child) => {
    const label = getCategoryLabel(child);
    if (label === 'Full Time' && !filterFullTime) return false;
    if (label === 'School Age' && !filterSchoolAge) return false;
    if (label === 'Part Time' && !filterPartTime) return false;
    if (label === 'After School' && !filterAfterSchool) return false;
    return true;
  };

  // Filter and sort children
  const filteredChildren = children
    .filter(matchesFilter)
    .sort((a, b) => {
      const nameA = a.name || `${a.lastName}, ${a.firstName}`;
      const nameB = b.name || `${b.lastName}, ${b.firstName}`;
      return nameA.localeCompare(nameB);
    });

  // Generate report HTML
  const generateReportHTML = () => {
    const today = new Date().toLocaleDateString('en-US', { 
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
    });
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Enrolled Children - ${organization?.name || 'Childcare Center'}</title>
        <style>
          @page { size: letter; margin: 0.5in; }
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { font-family: Arial, sans-serif; font-size: 11px; line-height: 1.4; color: #333; }
          .header { text-align: center; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 2px solid #1a365d; }
          .logo { display: flex; align-items: center; justify-content: center; gap: 10px; margin-bottom: 10px; }
          .logo-box { width: 40px; height: 40px; background: #1a365d; border-radius: 6px; display: flex; align-items: center; justify-content: center; }
          .logo-check { color: white; font-size: 20px; font-weight: bold; }
          .logo-text { text-align: left; }
          .logo-text-line { font-weight: bold; font-size: 14px; color: #1a365d; }
          .facility-name { font-size: 16px; font-weight: bold; color: #1a365d; margin-top: 10px; }
          .report-title { font-size: 18px; font-weight: bold; color: #1a365d; margin-top: 5px; }
          .report-date { font-size: 12px; color: #666; margin-top: 5px; }
          .summary { background: #f8f9fa; padding: 10px 15px; border-radius: 5px; margin-bottom: 15px; text-align: center; }
          .summary-number { font-size: 24px; font-weight: bold; color: #1a365d; }
          .summary-label { font-size: 11px; color: #666; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th { background: #1a365d; color: white; padding: 10px 12px; text-align: left; font-size: 12px; }
          td { padding: 10px 12px; border-bottom: 1px solid #ddd; font-size: 11px; }
          tr:nth-child(even) { background: #f8f9fa; }
          .child-name { font-weight: 500; }
          .category { font-size: 10px; padding: 3px 10px; border-radius: 10px; display: inline-block; }
          .category-fulltime { background: #d4edda; color: #155724; }
          .category-schoolage { background: #cce5ff; color: #004085; }
          .category-parttime { background: #fff3cd; color: #856404; }
          .category-afterschool { background: #e2e3e5; color: #383d41; }
          .footer { margin-top: 20px; padding-top: 10px; border-top: 1px solid #ddd; font-size: 10px; color: #666; text-align: center; }
          @media print { .no-print { display: none !important; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">
            <div class="logo-box"><span class="logo-check">✓</span></div>
            <div class="logo-text">
              <div class="logo-text-line">Simple</div>
              <div class="logo-text-line">Sign In</div>
            </div>
          </div>
          <div class="facility-name">${organization?.name || 'Childcare Center'}</div>
          <div class="report-title">Enrolled Children</div>
          <div class="report-date">${today}</div>
        </div>
        
        <div class="summary">
          <div class="summary-number">${filteredChildren.length}</div>
          <div class="summary-label">Total Enrolled Children</div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th style="width: 8%">#</th>
              <th style="width: ${showCategory ? '60%' : '92%'}">Child Name</th>
              ${showCategory ? '<th style="width: 32%">Category</th>' : ''}
            </tr>
          </thead>
          <tbody>
            ${filteredChildren.map((child, idx) => {
              const name = child.name || `${child.lastName}, ${child.firstName}`;
              const category = getCategoryLabel(child);
              const categoryClass = category.toLowerCase().replace(' ', '');
              
              return `
                <tr>
                  <td>${idx + 1}</td>
                  <td class="child-name">${name}</td>
                  ${showCategory ? `<td><span class="category category-${categoryClass}">${category}</span></td>` : ''}
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
        
        <div class="footer">
          Generated by Simple Sign In • ${new Date().toLocaleString()}
        </div>
      </body>
      </html>
    `;
  };

  // Print report
  const handlePrint = () => {
    const reportHTML = generateReportHTML();
    const printWindow = window.open('', '_blank');
    printWindow.document.write(reportHTML);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  // Email report
  const handleEmailReport = async () => {
    if (!recipientEmail || !recipientEmail.includes('@')) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    setSending(true);
    try {
      const reportHTML = generateReportHTML();
      
      await axios.post(`${API}/send-report-email`, {
        to: recipientEmail,
        subject: `Enrolled Children - ${organization?.name || 'Childcare Center'}`,
        htmlContent: reportHTML
      });
      
      toast.success(`Report sent to ${recipientEmail}`);
      setEmailDialogOpen(false);
      setRecipientEmail("");
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error("Failed to send email. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#1a365d]" />
              Enrolled Children Report
            </DialogTitle>
            <DialogDescription>
              Generate a report of all enrolled children for state licensing
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto">
            {/* Options */}
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <p className="font-medium text-sm text-gray-700 mb-3">Report Options</p>
              
              <div className="grid grid-cols-2 gap-4">
                {/* Show category toggle */}
                <div className="space-y-2">
                  <p className="text-xs text-gray-500 font-medium">Show Columns:</p>
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="show-category" 
                      checked={showCategory} 
                      onCheckedChange={setShowCategory}
                    />
                    <Label htmlFor="show-category" className="text-sm">Include Category</Label>
                  </div>
                </div>
                
                {/* Category filters */}
                <div className="space-y-2">
                  <p className="text-xs text-gray-500 font-medium">Filter by Category:</p>
                  <div className="grid grid-cols-2 gap-1">
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        id="filter-fulltime" 
                        checked={filterFullTime} 
                        onCheckedChange={setFilterFullTime}
                      />
                      <Label htmlFor="filter-fulltime" className="text-xs">Full Time</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        id="filter-schoolage" 
                        checked={filterSchoolAge} 
                        onCheckedChange={setFilterSchoolAge}
                      />
                      <Label htmlFor="filter-schoolage" className="text-xs">School Age</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        id="filter-parttime" 
                        checked={filterPartTime} 
                        onCheckedChange={setFilterPartTime}
                      />
                      <Label htmlFor="filter-parttime" className="text-xs">Part Time</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        id="filter-afterschool" 
                        checked={filterAfterSchool} 
                        onCheckedChange={setFilterAfterSchool}
                      />
                      <Label htmlFor="filter-afterschool" className="text-xs">After School</Label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Preview */}
            <div className="border rounded-lg p-4 bg-white">
              {/* Header with Simple Sign In Logo */}
              <div className="text-center mb-4 pb-4 border-b">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <div className="w-10 h-10 bg-[#1a365d] rounded-lg flex items-center justify-center">
                    <span className="text-white text-lg font-bold">✓</span>
                  </div>
                  <div className="text-left">
                    <p className="font-bold text-[#1a365d] text-sm leading-tight">Simple</p>
                    <p className="font-bold text-[#1a365d] text-sm leading-tight">Sign In</p>
                  </div>
                </div>
                <p className="font-bold text-[#1a365d]">{organization?.name || 'Childcare Center'}</p>
                <p className="text-sm text-gray-600">Enrolled Children</p>
                <p className="text-xs text-gray-500">{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
              </div>

              {/* Total Count */}
              <div className="text-center mb-4 bg-gray-50 py-3 rounded">
                <p className="text-3xl font-bold text-[#1a365d]">{filteredChildren.length}</p>
                <p className="text-xs text-gray-500">Total Enrolled Children</p>
              </div>

              {/* Table preview */}
              {loading ? (
                <div className="text-center py-8">Loading...</div>
              ) : (
                <div className="max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#1a365d] text-white sticky top-0">
                      <tr>
                        <th className="p-2 text-left w-10">#</th>
                        <th className="p-2 text-left">Child Name</th>
                        {showCategory && <th className="p-2 text-left w-32">Category</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredChildren.map((child, idx) => {
                        const name = child.name || `${child.lastName}, ${child.firstName}`;
                        const category = getCategoryLabel(child);
                        
                        return (
                          <tr key={child.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="p-2 text-gray-500">{idx + 1}</td>
                            <td className="p-2 font-medium">{name}</td>
                            {showCategory && (
                              <td className="p-2">
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  category === 'Full Time' ? 'bg-green-100 text-green-700' :
                                  category === 'School Age' ? 'bg-blue-100 text-blue-700' :
                                  category === 'Part Time' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {category}
                                </span>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
            <Button
              variant="outline"
              onClick={() => setEmailDialogOpen(true)}
              className="gap-2"
            >
              <Mail className="w-4 h-4" />
              Email Report
            </Button>
            <Button
              onClick={handlePrint}
              className="bg-[#1a365d] hover:bg-[#2d4a7c] gap-2"
            >
              <Printer className="w-4 h-4" />
              Print Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Email Report</DialogTitle>
            <DialogDescription>
              Send the enrolled children report to an email address
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="email">Recipient Email</Label>
            <Input
              id="email"
              type="email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              placeholder="inspector@state.gov"
              className="mt-1"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleEmailReport}
              disabled={sending}
              className="bg-[#1a365d] hover:bg-[#2d4a7c]"
            >
              {sending ? "Sending..." : "Send Email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EnrolledChildrenReport;
